import { computed, reactive } from 'vue'

import http from '@/services/http'
import type { ApiResponse } from '@/types/api'

/**
 * Configuración pública del sitio (datos de contacto, redes) gestionada desde
 * el admin. Se carga una sola vez y se comparte entre componentes.
 */
type SiteConfig = Record<string, string>

const DEFAULTS: SiteConfig = {
  sitio_nombre: 'Ariza Reyes Abogados',
  contacto_whatsapp: '573045559073',
  contacto_telefono: '3002945039',
  contacto_email: 'arizareyesabogados@gmail.com',
  contacto_direccion: '',
  contacto_horario: '',
  social_facebook: '',
  social_instagram: '',
  social_linkedin: '',
  sitio_logo_url: '',
  hero_badge: 'Bufete jurídico virtual · Colombia',
  hero_titulo: 'Defendemos lo que',
  hero_titulo_destacado: 'más te importa',
  hero_subtitulo:
    'Asesoría y representación legal de vanguardia. Gestiona tu caso en línea, agenda consultas y accede a tus documentos desde cualquier lugar.',
}

const state = reactive<{ config: SiteConfig; loaded: boolean }>({
  config: { ...DEFAULTS },
  loaded: false,
})
let inflight: Promise<void> | null = null

async function fetchConfig(force = false): Promise<void> {
  if (state.loaded && !force) return
  if (inflight) return inflight
  inflight = http
    .get<ApiResponse<SiteConfig>>('/public/site-config')
    .then(({ data }) => {
      if (data.data) state.config = { ...DEFAULTS, ...data.data }
      state.loaded = true
    })
    .catch(() => {
      /* mantiene los DEFAULTS si la API falla */
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

function waText(): string {
  const msg = encodeURIComponent('Hola, quisiera asesoría legal.')
  const num = (state.config.contacto_whatsapp || '').replace(/\D+/g, '')
  return `https://wa.me/${num}?text=${msg}`
}

export function useSiteConfig() {
  void fetchConfig()
  return {
    config: computed(() => state.config),
    whatsappUrl: computed(() => waText()),
    whatsappNumero: computed(() => state.config.contacto_whatsapp || ''),
    telefono: computed(() => state.config.contacto_telefono || ''),
    email: computed(() => state.config.contacto_email || ''),
    direccion: computed(() => state.config.contacto_direccion || ''),
    horario: computed(() => state.config.contacto_horario || ''),
    nombre: computed(() => state.config.sitio_nombre || 'Ariza Reyes Abogados'),
    logoUrl: computed(() => state.config.sitio_logo_url || ''),
    heroBadge: computed(() => state.config.hero_badge || ''),
    heroTitulo: computed(() => state.config.hero_titulo || ''),
    heroTituloDestacado: computed(() => state.config.hero_titulo_destacado || ''),
    heroSubtitulo: computed(() => state.config.hero_subtitulo || ''),
    social: computed(() => ({
      facebook: state.config.social_facebook || '',
      instagram: state.config.social_instagram || '',
      linkedin: state.config.social_linkedin || '',
    })),
    reload: fetchConfig,
  }
}
